/**
 * Byte ⇄ text conversions for the two auth adapters.
 *
 * Both a WebAuthn credential id and a PBKDF2 salt are raw bytes that have to
 * survive in a string-keyed preference store, and both are read back and handed
 * straight to a platform API. Standard base64 cannot do that job on its own:
 * `+` and `/` are legal in the alphabet but are also the characters a key-value
 * substrate, a URL, or a copied diagnostic is most likely to mangle, and a
 * mangled credential id fails as `NotAllowedError` — indistinguishable at the
 * call site from the user declining the prompt. base64url avoids the whole
 * class, and the padding is dropped because a decoder that tolerates its
 * absence cannot then be tripped by a store that trimmed it.
 */

/**
 * The platform crypto and WebAuthn APIs take `BufferSource`, which since
 * TypeScript 5.7 excludes a `SharedArrayBuffer`-backed view. Every array these
 * adapters produce is backed by a plain `ArrayBuffer`, so saying so keeps the
 * hand-off to `crypto.subtle` and `navigator.credentials` type-checked instead
 * of cast.
 */
export type ByteArray = Uint8Array<ArrayBuffer>;

const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*$/;

export function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * `null` rather than a throw for anything that is not base64url.
 *
 * The only caller is reading a value out of a store that any other code in the
 * origin could have written, and the honest answer to "this is not a credential
 * id" is "there is no enrolled credential" — which is a state the adapter
 * already handles. A throw here would surface as an unhandled rejection inside
 * an availability probe that is documented never to reject.
 */
export function decodeBase64Url(value: string): ByteArray | null {
  if (!BASE64URL_PATTERN.test(value)) return null;
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = padded.length % 4;
  // A length of exactly one more than a multiple of four cannot be base64: no
  // number of encoded bytes produces it.
  if (remainder === 1) return null;
  const full = remainder === 0 ? padded : padded + "=".repeat(4 - remainder);
  let binary: string;
  try {
    binary = atob(full);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

const TEXT_ENCODER = new TextEncoder();

export function encodeUtf8(value: string): ByteArray {
  return TEXT_ENCODER.encode(value);
}

/**
 * Length-independent comparison.
 *
 * The passphrase check record is compared with this rather than with `===` on a
 * decoded string. AES-GCM has already authenticated the record by the time we
 * get here, so this is belt-and-braces — but a byte comparison that returns
 * early is the kind of detail that gets copied into a place where it does
 * matter, and there is no cost to it being right here.
 */
export function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}
